import {
  CloseCircleOutlined,
  FilterOutlined,
  PlusOutlined,
  PlusCircleOutlined,
  SlidersOutlined,
} from '@ant-design/icons'
import {
  Button,
  Col,
  Empty,
  Input,
  message,
  Pagination,
  Row,
  Space,
  Badge,
  Popover,
} from 'antd'
import debounce from 'lodash/debounce'
import { inject, observer } from 'mobx-react'
import queryString from 'query-string'
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Helmet } from 'react-helmet'
import { useLocation, withRouter } from 'react-router-dom'

import AccessControlAction from '../../../components/AccessControlAction'
import { AvatarHasName } from '../../../components/Common/Avatar'
import { trimOverlengthString } from '../../../components/Common/CellText'
import { StatusTag } from '../../../components/Common/StatusTag'
import { TableBottomPaginationBlock } from '../../../components/Common/Table'
import TableComponent from '../../../components/Common/TableComponent'
import ContentBlockWrapper from '../../../components/ContentBlockWrapper'
import PageTitle from '../../../components/PageTitle'
import { systemName } from '../../../config'
import {
  ACL_ACTION_TYPE,
  ASSIGNEE_TYPE,
  DATE_FORMAT_DEFAULT,
  TYPE_STATUS,
} from '../../../constants'
import dayjs from '../../../dayjsConfig'
import { SearchBar } from '../../InternalMessage/InternalMessageSendPage/InternalMessageSendPageStyled'
import AdvanceFilterBlock from './AdvanceFilterBlock'
import PopupCreateSignDoc from './PopupCreateSignDoc'
import { FilterButtonWrapper } from './workDocumentSignDocPageStyled'
import DashboardLayout from '../../../layouts/DashboardLayout'
import AppPageHeader from '../../../components/AppPageHeader'

const { Search } = Input

const workDocumentSignDocPage = props => {
  const { history, electronicDocumentStore, loadingAnimationStore } = props
  const {
    electronicDocumentList,
    totalCount,
    queryParamsSignDocument,
  } = electronicDocumentStore

  const location = useLocation()
  const queryStringParse = queryString.parse(location.search)

  const [searchValue, setSearchValue] = useState(queryStringParse.title || '')
  const [showAdvanceFilter, setShowAdvanceFilter] = useState(false)
  const [
    isVisiblePopupCreateSignDoc,
    setIsVisiblePopupCreateSignDoc,
  ] = useState(false)
  const [countFilter, setCountFilter] = useState(0)
  const [isRefreshPage, setIsRefreshPage] = useState(true)
  const [checkEmpty, setCheckEmpty] = useState(true)

  // Use ref to prevent infinite loop
  const isInitialMount = useRef(true)
  const STORAGE_KEY = 'sign_document_pagination_state'

  const tableColumns = useMemo(
    () => [
      {
        title: 'Tiêu đề',
        render: record => trimOverlengthString(record.title, 450),
      },
      {
        title: 'Người trình ký',
        width: 180,
        render: record => {
          const creator = record.assignees.find(
            user => user.permission === ASSIGNEE_TYPE.CREATE
          )
          return creator ? (
            <AvatarHasName
              imgId={creator.image}
              name={creator.name_uppercase}
            />
          ) : (
            '-'
          )
        },
      },
      {
        align: 'center',
        title: 'Trạng thái',
        width: 100,
        render: record => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {StatusTag(TYPE_STATUS.ELECTRONIC_DOCUMENT, record.status, true)}
          </div>
        ),
      },
      {
        align: 'center',
        title: 'Ngày tạo',
        width: 120,
        render: record => (
          <span style={{ whiteSpace: 'nowrap' }}>
            {dayjs(record.created_date).format(DATE_FORMAT_DEFAULT)}
          </span>
        ),
      },
    ],
    []
  )

  // Memoize để tránh tạo lại hàm
  const getElectronicDocumentList = useCallback(async () => {
    try {
      loadingAnimationStore.setTableLoading(true)
      setCheckEmpty(false)
      await electronicDocumentStore.getElectronicDocumentList('WORK')
    } catch (error) {
      console.error(error)
      message.error(error?.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.setTableLoading(false)
      setCheckEmpty(true)
    }
  }, [electronicDocumentStore, loadingAnimationStore])

  // Initialize từ URL hoặc sessionStorage chỉ 1 lần
  useEffect(() => {
    const savedState = sessionStorage.getItem(STORAGE_KEY)

    if (savedState && !Object.keys(queryStringParse).length) {
      const parsedState = JSON.parse(savedState)
      const queryStringStringify = queryString.stringify(parsedState, {
        skipNull: true,
      })
      history.replace(`/archiving-n-distribution?${queryStringStringify}`)

      electronicDocumentStore.setQueryParamsSignDocument({
        page: parsedState.page || 0,
        size: parsedState.size || 10,
        title: parsedState.title || null,
        status: parsedState.status || null,
        from_date: parsedState.from_date || null,
        to_date: parsedState.to_date || null,
      })
    } else if (Object.keys(queryStringParse).length > 0) {
      electronicDocumentStore.setQueryParamsSignDocument({
        page: queryStringParse.page ? parseInt(queryStringParse.page) : 0,
        size: queryStringParse.size ? parseInt(queryStringParse.size) : 10,
        title: queryStringParse.title || null,
        status: queryStringParse.status || null,
        from_date: queryStringParse.from_date || null,
        to_date: queryStringParse.to_date || null,
      })
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(queryStringParse))
    } else {
      electronicDocumentStore.clearQueryParamsIncoming()
      sessionStorage.removeItem(STORAGE_KEY)
    }

    setIsRefreshPage(false)

    return () => {
      electronicDocumentStore.clearFilter()
      electronicDocumentStore.clearElectronicDocumentList()
    }
  }, []) // Chỉ chạy 1 lần khi mount

  // Fetch data khi queryParams thay đổi, nhưng skip lần đầu
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (isRefreshPage) return

    getElectronicDocumentList()
  }, [
    queryParamsSignDocument.page,
    queryParamsSignDocument.size,
    queryParamsSignDocument.title,
    queryParamsSignDocument.status,
    queryParamsSignDocument.from_date,
    queryParamsSignDocument.to_date,
    isRefreshPage,
    getElectronicDocumentList,
  ])

  // Cập nhật số lượng filter
  useEffect(() => {
    let count = 0
    if (queryStringParse.title) count++
    if (queryStringParse.status) count++
    if (queryStringParse.from_date && queryStringParse.to_date) count++
    setCountFilter(count)
  }, [
    queryStringParse.title,
    queryStringParse.status,
    queryStringParse.from_date,
    queryStringParse.to_date,
  ])

  // Đồng bộ search value từ URL
  useEffect(() => {
    setSearchValue(queryStringParse.title || '')
  }, [queryStringParse.title])

  const onChangePagination = useCallback(
    pageIndex => {
      const newParams = {
        ...queryStringParse,
        page: pageIndex - 1,
        size: 10,
      }

      electronicDocumentStore.setQueryParamsSignDocument({
        page: pageIndex - 1,
        size: 10,
        title: queryStringParse.title || null,
        status: queryStringParse.status || null,
        from_date: queryStringParse.from_date || null,
        to_date: queryStringParse.to_date || null,
      })

      const queryStringStringify = queryString.stringify(newParams, {
        skipNull: true,
      })
      history.replace(`/archiving-n-distribution?${queryStringStringify}`)
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newParams))
    },
    [electronicDocumentStore, queryStringParse, history]
  )

  // Tạo debounced function 1 lần duy nhất
  const debouncedSearch = useMemo(
    () =>
      debounce((title, currentQueryParams, store, historyObj) => {
        const newParams = {
          ...currentQueryParams,
          page: 0,
          size: 10,
          title: title ? title.trim() : null,
        }

        store.setQueryParamsSignDocument({
          page: 0,
          size: 10,
          title: title && title.trim(),
          status: currentQueryParams.status || null,
          from_date: currentQueryParams.from_date || null,
          to_date: currentQueryParams.to_date || null,
        })

        const queryStringStringify = queryString.stringify(newParams, {
          skipNull: true,
        })
        historyObj.replace(`/archiving-n-distribution?${queryStringStringify}`)
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newParams))
      }, 500),
    []
  )

  const handleQuickSearch = useCallback(
    title => {
      debouncedSearch(title, queryStringParse, electronicDocumentStore, history)
    },
    [debouncedSearch, queryStringParse, electronicDocumentStore, history]
  )

  const handleRowClick = useCallback(
    record => {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(queryStringParse))
      history.push(`/archiving-n-distribution/view/${record.code}`, {
        from: location.pathname + location.search,
      })
    },
    [queryStringParse, history, location.pathname, location.search]
  )

  return (
    <DashboardLayout>
      <Helmet>
        <title>Quản lý tài liệu số | Công việc {systemName}</title>
      </Helmet>
      <PageTitle
        location={props.location}
        title="Quản lý tài liệu số"
        hiddenGoBack
      />
      <ContentBlockWrapper>
        <AppPageHeader
          title="Quản lý tài liệu số"
          addon={
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {!showAdvanceFilter && (
                <Search
                  size="large"
                  className="rounded-search"
                  style={{ width: 300 }}
                  allowClear
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  onSearch={handleQuickSearch}
                  placeholder="Tìm kiếm trình ký theo tiêu đề..."
                />
              )}
              <div style={{ position: 'relative' }}>
                <Badge count={countFilter} size="small" offset={[-2, 2]}>
                  <Popover
                    content={<AdvanceFilterBlock onClose={() => setShowAdvanceFilter(false)} />}
                    trigger="click"
                    visible={showAdvanceFilter}
                    onVisibleChange={visible => setShowAdvanceFilter(visible)}
                    placement="bottomRight"
                    overlayClassName="custom-page-filter-dropdown-container">
                    <Button
                      size="large"
                      icon={<SlidersOutlined />}
                      style={{ borderRadius: 6 }}
                      type={showAdvanceFilter ? 'primary' : 'default'}
                    />
                  </Popover>
                </Badge>
              </div>
              <AccessControlAction
                aclActionType={ACL_ACTION_TYPE.create__DIGITAL_DOCUMENT}>
                <Button
                  size="large"
                  icon={<PlusCircleOutlined />}
                  style={{ borderRadius: 6 }}
                  onClick={() => setIsVisiblePopupCreateSignDoc(true)}
                  type={'primary'}>
                  Tạo mới
                </Button>
              </AccessControlAction>
            </div>
          }
        />
        <div>
          <TableComponent
            onRow={record => ({
              onClick: () => handleRowClick(record),
            })}
            rowKey={record => record.code}
            dataSource={electronicDocumentList}
            columns={tableColumns}
            pagination={false}
            scroll={{ x: 1000 }}
            locale={{
              emptyText:
                checkEmpty === true ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={'Không có dữ liệu'}
                  />
                ) : (
                  <div style={{ opacity: 0.1, height: '150px' }} />
                ),
            }}
            loading={{
              tip: 'Đang tải văn bản...',
              spinning: loadingAnimationStore.tableLoading,
            }}
          />

          <TableBottomPaginationBlock>
            <Pagination
              onChange={onChangePagination}
              showSizeChanger={false}
              showLessItems
              current={
                queryStringParse.page ? parseInt(queryStringParse.page) + 1 : 1
              }
              total={totalCount}
              hideOnSinglePage={true}
            />
          </TableBottomPaginationBlock>
        </div>
        {isVisiblePopupCreateSignDoc && (
          <PopupCreateSignDoc
            isVisiblePopupCreateSignDoc={isVisiblePopupCreateSignDoc}
            handleClosePopupCreateSignDoc={() =>
              setIsVisiblePopupCreateSignDoc(false)
            }
          />
        )}
      </ContentBlockWrapper>
    </DashboardLayout>
  )
}

workDocumentSignDocPage.propTypes = {}

export default memo(
  withRouter(
    inject(
      'loadingAnimationStore',
      'electronicDocumentStore',
      'departmentStore',
      'listHistoryLogStore'
    )(observer(workDocumentSignDocPage))
  )
)
